<template>
  <div class="settings-wrapper">
    <div class="settings">
      <email cardTitle="Billing" :tableData="billingList" :flag="1" @getDetail="getDetail" />
      <email style="margin-top: 20px;" cardTitle="Audit" :tableData="auditList" :flag="2" @getDetail="getDetail" />

      <div v-if="isAdmin" class="maintenance-card" v-loading="maintenanceLoading">
        <span class="status-badge" :class="isUnderMaintenance ? 'badge-active' : 'badge-inactive'">
          {{ isUnderMaintenance ? 'ACTIVE' : 'INACTIVE' }}
        </span>
        <div class="maintenance-header">
          <span class="header">Maintenance Mode</span>
          <el-switch
            v-model="isUnderMaintenance"
            active-color="#F56C6C"
            inactive-color="#DCDFE6"
            active-text="ON"
            inactive-text="OFF"
            @change="handleMaintenanceToggleChange"
          ></el-switch>
        </div>

        <div style="margin-top: 16px;">
          <p class="maintenance-label">Scheduled Maintenance Time</p>
          <p class="maintenance-hint">Users will be prompted 30 minutes before this time. Leave blank to skip the reminder.</p>
          <div style="display: flex; align-items: center; gap: 8px; margin-top: 8px;">
            <el-date-picker
              v-model="scheduledTime"
              type="datetime"
              placeholder="Select date and time"
              format="dd MMM yyyy HH:mm"
              value-format="yyyy-MM-dd HH:mm:ss"
              style="width: 260px;"
            ></el-date-picker>
            <el-button type="primary" size="small" :loading="maintenanceSaving" @click="handleMaintenanceSave">
              Save
            </el-button>
          </div>
        </div>

        <div v-if="generatedMessage" style="margin-top: 16px;">
          <p class="maintenance-label">Maintenance Message Preview</p>
          <p class="maintenance-hint">Auto-generated from the scheduled time. Shown as a banner and in the 30-min reminder popup.</p>
          <p class="maintenance-preview">{{ generatedMessage }}</p>
        </div>
      </div>

    </div>
  </div>
</template>
<script>
import { mapState } from "vuex";
import { getSystemEmail, getMaintenanceDetails, updateMaintenance } from "@/services/form"
import { resetMaintenanceCache } from "@/router"
import email from "./component/email.vue";
import { controlAcceptedBarge, getAcceptedBarge } from "@/services/user";
export default {
  name: "Settings",
  components: { email },
  data() {
    return {
      params: {
        pageNum: 1,
        pageSize: 9999,
      },
      billingList: [],
      auditList: [],
      controlAcceptedBarge: false,
      controlCoload: false,
      isUnderMaintenance: false,
      scheduledTime: null,
      maintenanceLoading: false,
      maintenanceSaving: false,
      maintenanceOriginal: null,
      scheduledRefreshTimeout: null,
    }
  },
  computed: {
    ...mapState(["userInfo", "screenType"]),
    isMobileScreen() {
      return this.screenType?.isMobileScreen ?? false;
    },
    isAdmin() {
      return Number(this.userInfo?.userType) == 0;
    },
    generatedMessage() {
      if (!this.scheduledTime) return '';
      const d = new Date(this.scheduledTime);
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const formatted = `${String(d.getDate()).padStart(2,'0')} ${months[d.getMonth()]} ${d.getFullYear()}, ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')} (SGT)`;
      return `The system will be under scheduled maintenance on ${formatted}. Please save your work and log out before ${formatted}.`;
    }
  },
  watch: {
    scheduledTime(val) {
      if (val && val.length >= 19) {
        this.scheduledTime = val.substring(0, 17) + '00';
      }
      clearTimeout(this.scheduledRefreshTimeout);
      if (val) {
        const msUntil = new Date(val) - new Date();
        if (msUntil > 0) {
          this.scheduledRefreshTimeout = setTimeout(() => {
            this.fetchMaintenanceStatus();
          }, msUntil);
        }
      }
    },
  },
  async created() {
    await this.getDetail()
    if (this.isAdmin) {
      await this.fetchMaintenanceStatus()
      if (this.scheduledTime) {
        const msUntil = new Date(this.scheduledTime) - new Date();
        if (msUntil > 0) {
          this.scheduledRefreshTimeout = setTimeout(() => {
            this.fetchMaintenanceStatus();
          }, msUntil);
        }
      }
    }
  },
  beforeDestroy() {
    clearTimeout(this.scheduledRefreshTimeout);
  },
  methods: {
    async getDetail() {
      this.loading = true
      await getSystemEmail(this.params).then(res => {
        if (res.data) {
          this.billingList = res?.data["billing"]
          this.auditList = res?.data["audit"]
        }
      }).finally(() => {
        this.loading = false
      });
      await getAcceptedBarge().then(res => {
        if (res.data) {
          this.controlAcceptedBarge = res.data.controlAcceptedBargeNomination;
          this.controlCoload = res.data.enableCoLoad;
        }
      })
    },
    async handleControlAcceptedBargeChange(value) {
      try {
        await this.$confirm(
          `Are you sure you want to ${value ? 'enable' : 'disable'} Control Accepted Barge?`,
          {
            confirmButtonText: 'Confirm',
            cancelButtonText: 'Cancel',
            type: 'warning',
            closeOnClickModal: false,
          }
        );
        try {
          await controlAcceptedBarge({
            controlAcceptedBargeNomination: value,
            enableCoLoad: this.controlCoload
          }).then(async res => {
            this.$message.success('Update accepted barge setting successfully');
            await this.getDetail();
          });
        } catch (error) {
          this.$message.error('Failed to update Control Accepted Barge setting');
          this.controlAcceptedBarge = !value;
        }
      } catch (e) {
        this.controlAcceptedBarge = !value;
        return;
      }
    },
    async handleCoload(value) {
      try {
        await this.$confirm(
          `Are you sure you want to ${value ? 'enable' : 'disable'} coload?`,
          {
            confirmButtonText: 'Confirm',
            cancelButtonText: 'Cancel',
            type: 'warning',
            closeOnClickModal: false,
          }
        );
        try {
          await controlAcceptedBarge({
            controlAcceptedBargeNomination: this.controlAcceptedBarge,
            enableCoLoad: value
          }).then(async () => {
            this.$message.success('Update co load setting successfully');
            await this.getDetail();
          });
        } catch (error) {
          this.$message.error('Failed to update Control Accepted Barge setting');
          this.controlAcceptedBarge = !value;
        }
      } catch (e) {
        this.controlAcceptedBarge = !value;
        return;
      }
    },
    async fetchMaintenanceStatus() {
      this.maintenanceLoading = true;
      try {
        const res = await getMaintenanceDetails();
        if (res && res.data) {
          this.isUnderMaintenance = !!res.data.isUnderMaintenance;
          this.scheduledTime = res.data.scheduledTime || null;
        }
        this.maintenanceOriginal = {
          isUnderMaintenance: this.isUnderMaintenance,
          scheduledTime: this.scheduledTime,
        };
      } catch (e) {
        this.$message.error('Failed to load maintenance settings.');
      } finally {
        this.maintenanceLoading = false;
      }
    },
    async handleMaintenanceSave() {
      this.maintenanceSaving = true;
      try {
        await updateMaintenance({
          value: this.generatedMessage,
          isUnderMaintenance: this.isUnderMaintenance,
          scheduledTime: this.scheduledTime || null,
        });
        this.maintenanceOriginal = {
          isUnderMaintenance: this.isUnderMaintenance,
          scheduledTime: this.scheduledTime,
        };
        resetMaintenanceCache();
        this.$message.success('Schedule time saved.');
      } catch (e) {
        this.$message.error('Failed to save schedule time.');
      } finally {
        this.maintenanceSaving = false;
      }
    },
    async handleMaintenanceToggleChange(val) {
      if (val) {
        try {
          await this.$confirm(
            'This will activate maintenance mode immediately. All non-admin users will be signed out. Are you sure?',
            'Turn On Maintenance',
            {
              confirmButtonText: 'Confirm',
              cancelButtonText: 'Cancel',
              type: 'warning',
              closeOnClickModal: false,
            }
          );
        } catch {
          this.$nextTick(() => { this.isUnderMaintenance = false; });
          return;
        }
        this.maintenanceSaving = true;
        try {
          await updateMaintenance({
            value: '',
            isUnderMaintenance: true,
            scheduledTime: null,
          });
          this.scheduledTime = null;
          this.maintenanceOriginal = {
            isUnderMaintenance: true,
            scheduledTime: null,
          };
          resetMaintenanceCache();
          this.$message.success('Maintenance mode activated.');
        } catch (e) {
          this.$message.error('Failed to activate maintenance mode.');
          this.$nextTick(() => { this.isUnderMaintenance = false; });
        } finally {
          this.maintenanceSaving = false;
        }
        return;
      }
      try {
        await this.$confirm(
          'This will turn off maintenance mode and clear the scheduled time. Are you sure?',
          'Turn Off Maintenance',
          {
            confirmButtonText: 'Confirm',
            cancelButtonText: 'Cancel',
            type: 'warning',
            closeOnClickModal: false,
          }
        );
      } catch {
        this.$nextTick(() => { this.isUnderMaintenance = true; });
        return;
      }
      this.maintenanceSaving = true;
      try {
        await updateMaintenance({
          value: '',
          isUnderMaintenance: false,
          scheduledTime: null,
        });
        this.scheduledTime = null;
        this.maintenanceOriginal = {
          isUnderMaintenance: false,
          scheduledTime: null,
        };
        resetMaintenanceCache();
        this.$message.success('Maintenance ended successfully.');
      } catch (e) {
        this.$message.error('Failed to end maintenance.');
        this.$nextTick(() => { this.isUnderMaintenance = true; });
      } finally {
        this.maintenanceSaving = false;
      }
    },
    handleMaintenanceReset() {
      if (this.maintenanceOriginal) {
        this.isUnderMaintenance = this.maintenanceOriginal.isUnderMaintenance;
        this.scheduledTime = this.maintenanceOriginal.scheduledTime;
      }
    },
  }
}
</script>
<style scoped lang="scss">
.settings {
  padding: 20px 20px;
  // background-color: #ffffff;
  border-radius: 10px;
  height: calc(100vh - 90px);
  overflow-y: auto;
}

.maintenance-card {
  position: relative;
  margin-top: 20px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 20px;
  background: #fff;

  .maintenance-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }

  .maintenance-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 20px;
  }

  .maintenance-label {
    font-size: 13px;
    font-weight: 600;
    color: #303133;
    margin: 0 0 2px 0;
  }

  .maintenance-hint {
    font-size: 12px;
    color: #909399;
    margin: 0;
  }

  .maintenance-preview {
    margin-top: 8px;
    font-size: 12px;
    color: #606266;
    background: #f4f4f5;
    border-radius: 4px;
    padding: 8px 12px;
    line-height: 1.6;
  }

  .status-badge {
    position: absolute;
    top: 16px;
    right: 16px;
    padding: 2px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 600;

    &.badge-active {
      background-color: #fef0f0;
      color: #f56c6c;
      border: 1px solid #fbc4c4;
    }

    &.badge-inactive {
      background-color: #f4f4f5;
      color: #909399;
      border: 1px solid #d3d4d6;
    }
  }
}
</style>
